import React, { useEffect, useState } from 'react';
import styles from './PastTrainings.module.css';
import { getAuthToken, useFetchWithAuth } from '../lib/auth';
import { HOST } from '../config/api';
import { getExerciseCategoryLabel } from '../utils/exerciseCategoryLabels';

const PastTrainings = ({ userId, refresh, demoMode = false, historyOverride = null }) => {
    const [trainingHistory, setTrainingHistory] = useState([]);

    const fetchTrainingHistory = useFetchWithAuth(HOST + `/v1/training_history?userid=${userId}`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });

    useEffect(() => {
        if (demoMode) {
            setTrainingHistory(historyOverride || []);
            return;
        }

        const fetchData = async () => {
            try {
                const data = await fetchTrainingHistory();
                if (data?.training) {
                    setTrainingHistory(data.training);
                }
            } catch (error) {
                console.error('Failed to load training history', error);
            }
        };

        fetchData();
    }, [demoMode, fetchTrainingHistory, historyOverride, refresh, userId]);

    return (
        <section className={styles.historySection}>
            <div className={styles.headerRow}>
                <div>
                    <span className={styles.kicker}>Recent history</span>
                    <h2 className={styles.header}>Review your recent sessions.</h2>
                    <p className={styles.description}>
                        A compact record of what you completed, where sessions struggled, and how your recent training rhythm looks.
                    </p>
                </div>
            </div>

            {trainingHistory.length === 0 ? (
                <div className={styles.emptyState}>
                    <strong>No completed trainings yet.</strong>
                    <p>Your finished sessions will appear here once you submit your first training.</p>
                </div>
            ) : (
                <div className={styles.historyList}>
                    {trainingHistory.map((training, index) => {
                        const total = training.exerciseHistoryModels?.length || 0;
                        const completed = training.exerciseHistoryModels?.filter((exercise) => exercise.completed === 1).length || 0;
                        const failed = total - completed;

                        return (
                            <article key={`${training.timestamp}-${index}`} className={styles.historyCard}>
                                <div className={styles.historyTop}>
                                    <div>
                                        <strong className={styles.trainingName}>{training.name || 'Training session'}</strong>
                                        <p className={styles.trainingTimestamp}>{new Date(training.timestamp).toLocaleString()}</p>
                                    </div>
                                    <div className={styles.resultPills}>
                                        <span className={styles.completedPill}>{completed} completed</span>
                                        <span className={styles.failedPill}>{failed} failed</span>
                                    </div>
                                </div>

                                <div className={styles.exerciseRows}>
                                    {(training.exerciseHistoryModels || []).map((exercise, exIndex) => (
                                        <div
                                            key={`${training.timestamp}-${exIndex}`}
                                            className={`${styles.exerciseRow} ${exercise.completed === 1 ? styles.exerciseCompleted : styles.exerciseFailed}`}
                                        >
                                            <div className={styles.exerciseIndex}>{exIndex + 1}</div>
                                            <div className={styles.exerciseCopy}>
                                                <strong>{getExerciseCategoryLabel(exercise.category)}</strong>
                                                <span>{exercise.description || 'Completed training entry'}</span>
                                                {exercise.comment ? (
                                                    <div className={styles.exerciseComment}>
                                                        <small>Your comment</small>
                                                        <p>{exercise.comment}</p>
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className={`${styles.rowStatus} ${exercise.completed === 1 ? styles.rowStatusDone : styles.rowStatusFailed}`}>
                                                {exercise.completed === 1 ? 'Completed' : 'Failed'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </section>
    );
};

export default PastTrainings;
